/**
 * Búsqueda por foto en la SPA: textos de coincidencia, patentes casi iguales y la
 * preparación de la foto del caso antes de enviarla (se achica en el navegador:
 * Caddy corta los cuerpos en 1 MB y la huella no necesita más de 1280 px).
 *
 * Que el otro aviso tenga OTRA patente no se marca como alerta: revisados a ojo 40
 * pares al azar de producción (2026-11-02), ninguno era el mismo auto con la patente
 * cambiada. Eran publicidad o afiches compartidos en muchas publicaciones, fotos de
 * automotoras y avisos de grupos que mezclan fotos y textos de otras publicaciones.
 * Tampoco sirve la patente leída en la misma foto: el OCR en la nube recibe varias
 * fotos a la vez y a veces atribuye la lectura a la foto equivocada. Lo que sí
 * aparecía (5 de 40) era la misma patente leída distinto: eso se marca.
 */

import type { PhotoMatchBrief, PhotoMatchTier } from "../../shared/contracts.js";

export const TIER_LABELS: Record<PhotoMatchTier, string> = {
  misma: "Misma foto",
  muy_parecida: "Parecida",
};

export const TIER_HINTS: Record<PhotoMatchTier, string> = {
  misma: "La misma foto, recomprimida o recortada",
  muy_parecida: "Mismo encuadre, plantilla o lugar: puede ser otro vehículo. Compare las fotos.",
};

/** Distancia de edición con transposición de vecinos (Damerau restringida). */
export function plateDistance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, d[i - 2]![j - 2]! + 1);
      d[i]![j] = v;
    }
  }
  return d[a.length]![b.length]!;
}

/**
 * Patentes casi iguales entre dos avisos (1–2 caracteres de diferencia, ninguna
 * compartida): la misma patente leída o escrita distinto, p. ej. una letra que el OCR
 * confundió o se saltó. Solo con avisos que comparten foto, donde el azar no explica
 * el parecido.
 */
export function nearPlates(base: readonly string[], other: readonly string[]): [string, string][] {
  if (base.some((p) => other.includes(p))) return [];
  const out: [string, string][] = [];
  for (const a of base) for (const b of other) {
    if (Math.min(a.length, b.length) >= 5 && plateDistance(a, b) <= 2) out.push([a, b]);
  }
  return out;
}

/**
 * Resumen para la tarjeta del caso: "Misma foto en 3 avisos" (null si no hay). Solo
 * cuenta la misma foto; las parecidas pueden ser otro vehículo y quedan en la ficha.
 */
export function photoMatchBadge(briefs: readonly PhotoMatchBrief[] | undefined, basePlates: readonly string[]): {
  text: string;
  title: string;
  /** Algún otro aviso tiene una patente casi igual (posible error de lectura). */
  nearPlate: boolean;
} | null {
  const same = (briefs ?? []).filter((b) => b.tier === "misma");
  if (same.length === 0) return null;
  const n = same.length;
  const near = same.flatMap((b) => nearPlates(basePlates, b.plates));
  const text = `Misma foto en ${n} ${n === 1 ? "aviso" : "avisos"}`;
  const title = near.length > 0
    ? `${text}; patente casi igual (${[...new Set(near.map(([a, b]) => `${a} / ${b}`))].join(", ")}): puede ser la misma patente mal leída`
    : text;
  return { text, title, nearPlate: near.length > 0 };
}

/** Lado mayor de la foto del caso que se envía. */
export const UPLOAD_MAX_SIDE = 1280;
/** Tope del archivo original (el navegador lo achica antes de enviarlo). */
export const UPLOAD_MAX_FILE = 25 * 1024 * 1024;

/** Tamaño final manteniendo la proporción. */
export function fitWithin(w: number, h: number, max = UPLOAD_MAX_SIDE): { w: number; h: number } {
  const k = Math.min(1, max / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/** Achica la foto en el navegador y la devuelve en base64 (JPEG), sin el prefijo data:. */
export async function prepareUpload(file: Blob): Promise<string> {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  const { w, h } = fitWithin(bmp.width, bmp.height);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext("2d");
  if (!g) throw new Error("El navegador no pudo procesar la foto");
  g.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  for (const quality of [0.85, 0.7, 0.55]) {
    const url = canvas.toDataURL("image/jpeg", quality);
    const b64 = url.slice(url.indexOf(",") + 1);
    if (b64.length <= 950_000) return b64;
  }
  throw new Error("La foto es demasiado grande incluso achicada");
}
